'use client';

/**
 * MiniCart Dropdown
 *
 * Displays a dropdown panel with cart items, subtotal, and quick actions.
 * Shown on hover/click of the cart icon in the header.
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useCart, type CartItem } from '@/contexts/CartContext';

// ============================================================================
// Helpers
// ============================================================================

function formatPrice(price: string | number): string {
  const num = typeof price === 'string' ? parseFloat(price) : price;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(num);
}
function getItemImage(item: CartItem): {
  url: string;
  alt: string;
} {
  const mainImage = item.product.images.find(img => img.role === 'main');
  const image = mainImage || item.product.images[0];
  return {
    url: image?.url || '/images/placeholder-product.png',
    alt: image?.altText || item.product.name
  };
}
function getSessionId(): string | null {
  return typeof window !== 'undefined' ? localStorage.getItem('cart_session_id') : null;
}

// ============================================================================
// Sub-components
// ============================================================================

interface MiniCartItemProps {
  item: CartItem;
  onQuantityUpdate: (itemId: number, quantity: number) => void;
  onRemove: (itemId: number) => void;
  isUpdating: boolean;
}
function MiniCartItem(t0) {
  const $ = _c(49);
  const {
    item,
    onQuantityUpdate,
    onRemove,
    isUpdating
  } = t0;
  const [detailsOpen, setDetailsOpen] = useState(false);
  let t1;
  if ($[0] !== item) {
    t1 = getItemImage(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const image = t1;
  const hasOptions = item.selectedOptions && item.selectedOptions.length > 0;
  const t2 = `/${item.product.slug}`;
  let t3;
  if ($[2] !== image.alt || $[3] !== image.url) {
    t3 = <Image src={image.url} alt={image.alt} width={75} height={75} className="object-contain" />;
    $[2] = image.alt;
    $[3] = image.url;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Link href={t2} className="mini-cart-item-image">{t3}</Link>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = `/${item.product.slug}`;
  let t6;
  if ($[8] !== item.product.name || $[9] !== t5) {
    t6 = <Link href={t5} className="mini-cart-item-name">{item.product.name}</Link>;
    $[8] = item.product.name;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== detailsOpen || $[12] !== hasOptions) {
    t7 = hasOptions && <button type="button" className="mini-cart-see-details" onClick={() => setDetailsOpen(!detailsOpen)}>See Details {detailsOpen ? "\u25B4" : "\u25BE"}</button>;
    $[11] = detailsOpen;
    $[12] = hasOptions;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== detailsOpen || $[15] !== hasOptions || $[16] !== item.selectedOptions) {
    t8 = detailsOpen && hasOptions && <ul className="mini-cart-options">{item.selectedOptions.map(_temp)}</ul>;
    $[14] = detailsOpen;
    $[15] = hasOptions;
    $[16] = item.selectedOptions;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== item.calculatedPrice) {
    t9 = formatPrice(item.calculatedPrice);
    $[18] = item.calculatedPrice;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <div className="mini-cart-item-price">{t9}</div>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== item.id || $[23] !== onQuantityUpdate) {
    t11 = e => {
      const qty = parseInt(e.target.value, 10);
      if (qty >= 1 && qty <= 999) {
        onQuantityUpdate(item.id, qty);
      }
    };
    $[22] = item.id;
    $[23] = onQuantityUpdate;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== isUpdating || $[26] !== item.quantity || $[27] !== t11) {
    t12 = <label className="mini-cart-qty-label">Qty:<input type="number" min="1" max="999" value={item.quantity} disabled={isUpdating} onChange={t11} className="mini-cart-qty-input" /></label>;
    $[25] = isUpdating;
    $[26] = item.quantity;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== item.id || $[30] !== onRemove) {
    t13 = () => onRemove(item.id);
    $[29] = item.id;
    $[30] = onRemove;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const t14 = `Remove ${item.product.name} from cart`;
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <TrashIcon />;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== isUpdating || $[34] !== t13 || $[35] !== t14) {
    t16 = <button type="button" onClick={t13} disabled={isUpdating} className="mini-cart-remove-btn" aria-label={t14}>{t15}</button>;
    $[33] = isUpdating;
    $[34] = t13;
    $[35] = t14;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t12 || $[38] !== t16) {
    t17 = <div className="mini-cart-item-actions">{t12}{t16}</div>;
    $[37] = t12;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t10 || $[41] !== t17 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8) {
    t18 = <div className="mini-cart-item-details">{t6}{t7}{t8}{t10}{t17}</div>;
    $[40] = t10;
    $[41] = t17;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t18 || $[47] !== t4) {
    t19 = <div className="mini-cart-item">{t4}{t18}</div>;
    $[46] = t18;
    $[47] = t4;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  return t19;
}

// ============================================================================
// Main Component
// ============================================================================
function _temp(opt) {
  return <li key={`${opt.optionId}-${opt.valueId}`}><span className="mini-cart-option-label">{opt.title || `Option ${opt.optionId}`}:</span>{" "}{opt.valueTitle || `Value ${opt.valueId}`}</li>;
}
export function MiniCart() {
  const {
    items,
    itemCount,
    subtotal
  } = useCart();
  const [updatingItems, setUpdatingItems] = useState<Set<number>>(new Set());
  const handleQuantityUpdate = useCallback(async (itemId: number, quantity: number) => {
    const sessionId = getSessionId();
    if (!sessionId) return;
    setUpdatingItems(prev => new Set(prev).add(itemId));
    try {
      const response = await fetch(`/api/cart/items/${itemId}?sessionId=${sessionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quantity
        })
      });
      if (response.ok) {
        window.dispatchEvent(new CustomEvent('cart-updated'));
      }
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  }, []);
  const handleRemove = useCallback(async (itemId_0: number) => {
    const sessionId_0 = getSessionId();
    if (!sessionId_0) return;
    setUpdatingItems(prev_0 => new Set(prev_0).add(itemId_0));
    try {
      const response = await fetch(`/api/cart/items/${itemId_0}?sessionId=${sessionId_0}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        window.dispatchEvent(new CustomEvent('cart-updated'));
      }
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId_0);
        return next;
      });
    }
  }, []);

  // Empty cart state
  if (items.length === 0) {
    return <div className="mini-cart-dropdown">
        <div className="mini-cart-empty">
          You have no items in your shopping cart.
        </div>
      </div>;
  }
  return <div className="mini-cart-dropdown">
      {/* Header */}
      <div className="mini-cart-header">
        {itemCount} {itemCount === 1 ? 'item' : 'items'} in cart
      </div>

      {/* Items list */}
      <div className="mini-cart-items">
        {items.map(item => <MiniCartItem key={item.id} item={item} onQuantityUpdate={handleQuantityUpdate} onRemove={handleRemove} isUpdating={updatingItems.has(item.id)} />)}
      </div>

      {/* Subtotal */}
      <div className="mini-cart-subtotal">
        <span>Cart Subtotal:</span>
        <span className="mini-cart-subtotal-amount">{formatPrice(subtotal)}</span>
      </div>

      {/* Actions */}
      <div className="mini-cart-actions">
        <Link href="/checkout" className="mini-cart-checkout-btn">
          Proceed to Checkout
        </Link>
        <Link href="/checkout/cart" className="mini-cart-view-cart-link">
          VIEW AND EDIT CART
        </Link>
      </div>
    </div>;
}

// ============================================================================
// Icons
// ============================================================================

function TrashIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}